import { forwardRef, useRef, useState } from "react";
import { toPng } from "html-to-image";
import { Download, Share2, Copy, Check } from "lucide-react";

import { PRICE } from "../data/collections";
import { PERSONALITIES, SITE } from "../data/site";
import { PrimaryDoodle, SecondaryDoodle } from "./PersonalityDoodles";
import { Star, SunDoodle } from "./Illustrations";
import { Scribble } from "./Doodles";

/** Card a parent can download & share to Instagram after taking the quiz.
 *  Rendered visually at a fixed 1080×1350 (4:5 IG portrait) inside a scaled wrapper. */
export default function ShareCard({ collection, answers }) {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const personality = PERSONALITIES.find((p) => p.slug === collection.personality);
  const colorName = collection.colors[0];

  const caption = `Just took the SOL Kids quiz and matched with the ${collection.name} ☀️\n"${collection.tagline[0]}"\n\nGet yours at @${SITE.instagram.replace("@", "")} · ${PRICE} · Shipping charges extra, as applicable\n\n#SOLKids #LittleAdventures #KidsSunglasses #IndianKids`;

  const download = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#FAFAF7",
      });
      const link = document.createElement("a");
      link.download = `sol-kids-match-${collection.slug}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      console.error("ShareCard: PNG generation failed", e);
      // eslint-disable-next-line no-alert
      alert("Couldn't generate the card. Try again?");
    } finally {
      setDownloading(false);
    }
  };

  const shareNative = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await toPng(cardRef.current, { pixelRatio: 2 });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `sol-kids-${collection.slug}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "My SOL Kids match", text: caption });
      } else {
        await download();
      }
    } catch (e) {
      // AbortError = user dismissed the share sheet; anything else is a real failure
      if (e?.name !== "AbortError") console.error("ShareCard: native share failed", e);
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      console.error("ShareCard: clipboard write failed", e);
    }
  };

  return (
    <div className="mt-14 md:mt-20" data-testid="share-card-section">
      <div className="text-center mb-8">
        <p className="eyebrow mb-2">Share their match</p>
        <h3 className="font-serif text-3xl md:text-4xl tracking-[-0.02em]">
          Show off their <span className="italic" style={{ color: "#DD2E20" }}>SOL pair.</span>
        </h3>
        <p className="mt-2 text-[#595955] text-sm">Tag <span className="font-medium text-[#2C2C2A]">{SITE.instagram}</span> when you post — we love a feature.</p>
      </div>

      <div className="grid md:grid-cols-12 gap-10 items-start">
        {/* Preview (scaled) */}
        <div className="md:col-span-7">
          <div className="mx-auto max-w-[420px] aspect-[4/5] rounded-[20px] overflow-hidden shadow-[0_30px_70px_rgba(0,0,0,0.14)] border border-[#2C2C2A]/5">
            {/* Scale wrapper — render at native 1080x1350, scale to fit */}
            <div className="origin-top-left" style={{ transform: "scale(0.389)", width: 1080, height: 1350 }}>
              <CardArt ref={cardRef} collection={collection} personality={personality} colorName={colorName} />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="md:col-span-5 flex flex-col gap-3">
          <button
            onClick={download}
            disabled={downloading}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2C2C2A] text-[#FAFAF7] px-6 py-4 text-[15px] font-medium hover:bg-[#1A1A18] transition-colors disabled:opacity-60"
            data-testid="share-download"
          >
            <Download className="w-4 h-4" /> {downloading ? "Building card…" : "Download card (PNG)"}
          </button>
          <button
            onClick={shareNative}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#F4C734] text-[#2C2C2A] px-6 py-4 text-[15px] font-medium hover:brightness-95 transition-all"
            data-testid="share-native"
          >
            <Share2 className="w-4 h-4" /> Share to Instagram
          </button>
          <button
            onClick={copyCaption}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-[#2C2C2A] border border-[#2C2C2A]/15 px-6 py-4 text-[15px] font-medium hover:border-[#2C2C2A] transition-colors"
            data-testid="share-copy-caption"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Caption copied" : "Copy Instagram caption"}
          </button>
          <details className="mt-2 rounded-2xl bg-[#F3EADA] p-4 text-[13px] text-[#595955] leading-relaxed">
            <summary className="cursor-pointer font-medium text-[#2C2C2A]">Suggested caption</summary>
            <pre className="mt-3 whitespace-pre-wrap font-sans text-[13px] text-[#2C2C2A]/85">{caption}</pre>
          </details>
        </div>
      </div>
    </div>
  );
}

/** The actual 1080×1350 card composition. */
const CardArt = forwardRef(function CardArt({ collection, personality, colorName }, ref) {
  return (
    <div
      ref={ref}
      className="relative"
      style={{ width: 1080, height: 1350, background: "#FAFAF7", fontFamily: "Fraunces, Georgia, serif" }}
      data-testid="share-card-art"
    >
      {/* Colour-block bands */}
      <div style={{ position: "absolute", inset: 0, display: "flex" }} aria-hidden>
        <div style={{ flex: 1, background: "#F4C734" }} />
        <div style={{ flex: 1, background: "#EE8C72" }} />
        <div style={{ flex: 1, background: "#8BC547" }} />
        <div style={{ flex: 1, background: "#A9CDE5" }} />
        <div style={{ flex: 1, background: "#C6B9D6" }} />
      </div>
      {/* Inner card */}
      <div style={{ position: "absolute", inset: 36, background: "#FAFAF7", borderRadius: 36, padding: 56, overflow: "hidden" }}>
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <img src="/logo-trim.png" alt="SOL Kids" crossOrigin="anonymous" style={{ height: 80, width: "auto" }} />
          <p style={{ fontFamily: "Outfit, system-ui, sans-serif", fontSize: 18, letterSpacing: "0.22em", textTransform: "uppercase", color: "#595955", fontWeight: 600, margin: 0 }}>
            My match · SS ‘26
          </p>
        </div>

        {/* Doodles */}
        <div style={{ position: "absolute", top: 100, right: 60 }}>
          <SunDoodle size={70} color="#F4C734" />
        </div>
        <div style={{ position: "absolute", top: 230, left: 60 }}>
          <Star size={30} color="#DD2E20" />
        </div>
        <div style={{ position: "absolute", bottom: 280, left: 90 }}>
          <PrimaryDoodle personality={collection.personality} className="!w-[70px] !h-[70px]" />
        </div>
        <div style={{ position: "absolute", bottom: 220, right: 110 }}>
          <SecondaryDoodle personality={collection.personality} className="!w-[60px] !h-[60px]" />
        </div>

        {/* Main visual */}
        <div style={{ marginTop: 60, display: "flex", justifyContent: "center" }}>
          <div style={{ width: 540, height: 540, borderRadius: 28, overflow: "hidden", background: "#F3EADA" }}>
            <img
              src={`/products/${collection.slug}-kid.jpg`}
              alt={collection.name}
              crossOrigin="anonymous"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        </div>

        {/* Text */}
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <p style={{ fontFamily: "Outfit, system-ui, sans-serif", fontSize: 20, letterSpacing: "0.22em", textTransform: "uppercase", color: "#595955", margin: 0 }}>
            {personality?.title || "Their match"}
          </p>
          <h2 style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 132, lineHeight: 1, margin: "16px 0 0", letterSpacing: "-0.03em", fontStyle: "italic", color: "#DD2E20" }}>
            {collection.name}
          </h2>
          <p style={{ fontFamily: "Fraunces, Georgia, serif", fontSize: 28, margin: "20px 0 0", color: "#2C2C2A", lineHeight: 1.25, maxWidth: 760, marginLeft: "auto", marginRight: "auto" }}>
            "{collection.tagline[0]}"
          </p>
        </div>

        {/* Footer pill */}
        <div style={{ position: "absolute", bottom: 56, left: 56, right: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ width: 28, height: 28, borderRadius: 999, background: "#F4C734", border: "1.5px solid rgba(44,44,42,0.18)" }} />
            <p style={{ fontFamily: "Outfit, system-ui, sans-serif", fontSize: 18, color: "#2C2C2A", margin: 0, fontWeight: 500 }}>
              {(colorName || "").toUpperCase()} · {PRICE}
            </p>
          </div>
          <p style={{ fontFamily: "Outfit, system-ui, sans-serif", fontSize: 18, color: "#2C2C2A", margin: 0, fontWeight: 600 }}>
            {SITE.instagram}
          </p>
        </div>
      </div>
    </div>
  );
});
